import {Component, OnInit, OnDestroy} from '@angular/core';
import {ServiceRequest} from '../../../Models/ServiceRequest';
import {ActivatedRoute} from '@angular/router';
import {ServiceRequestService} from '../../../Services/service-request.service';
import {Helper} from '../../../../helpers/helper.service';
import {IButton} from '../../../../shared/card/card.component';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '../../../../../environments/environment';
import {animate, style, state, transition, trigger} from '@angular/animations';
import {Subscription} from 'rxjs';
import {Product} from '../../../Models/Product';
import {Option} from '../../../Models/Option';
import {carpetOptionName, carpetRoleLabelKey, carpetSizeLabel} from '../../../../helpers/carpet-price';

@Component({
  selector: "app-view-service-request",
  templateUrl: "./view-service-request.component.html",
  styleUrls: [
    "./view-service-request.component.scss",
    "../../../../../assets/icon/timeline/style.css",
  ],
  animations: [
    trigger("openClose", [
      state("true", style({ height: "*" })),
      state("false", style({ height: "0px" })),
      transition("false <=> true", animate("500ms ease")),
    ]),
  ],
})
export class ViewServiceRequestComponent implements OnInit, OnDestroy {
  public baseUrl = environment.apiUrl;
  public requestId: number;
  public archiveId: number;
  public moreButton: IButton;
  public showMore = false;
  public editStatus = false;
  public editPriority = false;
  public statuses = [];
  public priorities = [];
  public request: ServiceRequest = new ServiceRequest();

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly requestService: ServiceRequestService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit() {
    this.showLessDetail();
    this.route.params.subscribe((params) => {
      if (Helper.isDefined(params["id"]) && this.requestId !== params["id"]) {
        this.requestId = params["id"];
        this.getRequestItem();
      }
      if (Helper.isDefined(params["replyId"])) {
        this.archiveId = params["replyId"];
      } else {
        this.archiveId = null;
      }
      this.requestService.serviceRequest.next(this.request);
    });
    this.subscriptions.push(
      this.requestService.serviceRequest.subscribe((serviceRequest) => {
        this.request = serviceRequest;
        if (this.archiveId && serviceRequest.canceledReplies) {
          serviceRequest.reply = serviceRequest.canceledReplies.find(
            (v) => v.id === +this.archiveId,
          );
        }
        serviceRequest.isArchived = !!this.archiveId;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  showMoreDetail() {
    this.showMore = true;
    this.moreButton = {
      key: "moreDetails",
      btnName: this.translate.instant(
        "admin.service.request.view.less_details",
      ),
      method: "showLessDetail",
      btnCustomClass: "btn-default",
      btnIconAfter: "fa fa-arrow-up",
    };
  }

  showLessDetail() {
    this.showMore = false;
    this.moreButton = {
      key: "moreDetails",
      btnName: this.translate.instant(
        "admin.service.request.view.more_details",
      ),
      method: "showMoreDetail",
      btnCustomClass: "btn-default",
      btnIconAfter: "fa fa-arrow-right",
    };
  }

  getRequestItem() {
    this.requestService.fetchServiceRequestComplete(this.requestId);
    this.getStatuses();
    this.getPriorities();
  }

  calFunction(method: string) {
    this[method]();
  }

  changeEditStatus() {
    this.editStatus = !this.editStatus;
  }

  changeEditPriority() {
    this.editPriority = !this.editPriority;
  }

  getFontAwesomeIconFromMIME(mimeType) {
    const icon_classes = {
      // Media
      image: "fa-file-image-o",
      audio: "fa-file-audio-o",
      video: "fa-file-video-o",
      // Documents
      "application/pdf": "fa-file-pdf-o",
      "application/msword": "fa-file-word-o",
      "application/vnd.ms-word": "fa-file-word-o",
      "application/vnd.oasis.opendocument.text": "fa-file-word-o",
      "application/vnd.openxmlformats-officedocument.wordprocessingml":
        "fa-file-word-o",
      "application/vnd.ms-excel": "fa-file-excel-o",
      "application/vnd.openxmlformats-officedocument.spreadsheetml":
        "fa-file-excel-o",
      "application/vnd.oasis.opendocument.spreadsheet": "fa-file-excel-o",
      "application/vnd.ms-powerpoint": "fa-file-powerpoint-o",
      "application/vnd.openxmlformats-officedocument.presentationml":
        "fa-file-powerpoint-o",
      "application/vnd.oasis.opendocument.presentation": "fa-file-powerpoint-o",
      "text/plain": "fa-file-text-o",
      "text/html": "fa-file-code-o",
      "application/json": "fa-file-code-o",
      // Archives
      "application/gzip": "fa-file-archive-o",
      "application/zip": "fa-file-archive-o",
    };

    for (const key in icon_classes) {
      if (icon_classes.hasOwnProperty(key)) {
        if (mimeType.search(key) === 0) {
          return icon_classes[key];
        }
      } else {
        return "fa-file-o";
      }
    }
  }

  getStatuses() {
    this.requestService.getServiceRequestStatuses().subscribe((res) => {
      this.statuses = res["statuses"];
    });
  }

  getPriorities() {
    this.requestService.getServiceRequestPriorities().subscribe((res) => {
      this.priorities = res["priorities"];
    });
  }

  savePriority($event) {
    this.requestService
      .updatePriority(this.request.id, $event.id)
      .subscribe((res) => {
        if (res) {
          this.request.priority = this.priorities.find(
            (priority) => priority.id == $event.id,
          );
          this.changeEditPriority();
        }
      });
  }

  saveStatus($event) {
    this.requestService
      .updateStatus(this.request.id, $event.id)
      .subscribe((res) => {
        if (res) {
          this.request.status = this.statuses.find(
            (status) => status.id === $event.id,
          );
          this.changeEditStatus();
        }
      });
  }

  /** Carpet roles, exposed for the template so it doesn't hardcode magic strings */
  public readonly CARPET_ROLE_SHAPE = Option.CARPET_ROLE_SHAPE;
  public readonly CARPET_ROLE_FINISHING = Option.CARPET_ROLE_FINISHING;
  public readonly CARPET_ROLE_COLOUR = Option.CARPET_ROLE_COLOUR;

  /** A rug has no fabric — same check manage-order.component.ts uses (Product.isRug) */
  isRugProduct(productItem): boolean {
    return Product.isRug(productItem?.product);
  }

  /**
   * Shape/finishing/colour for a rug element, read off its own orderItemOptions.
   * D9: the payload names the Option `urbanOption`, never `option`, and the role sits on
   * the parent group as often as on the leaf — both are handled by carpetOptionName.
   */
  rugOptionName(holder, carpetRole: string): string {
    return carpetOptionName(holder?.orderItemOptions, carpetRole);
  }

  carpetSizeLabel = carpetSizeLabel;
  carpetRoleLabelKey = carpetRoleLabelKey;

  getReplyItemIconClass(actionName) {
    switch (actionName) {
      case "email":
        return "fa fa-envelope-o";
      case "confirmation":
        return "fa fa-check-square-o";
      case "text":
        return "timeline icon-text";
      case "invoice":
        return "timeline icon-invoice";
      case "packaging_label":
        return "timeline icon-packaging-label";
      case "re_order":
        return "timeline icon-reorder";
      case "form":
        return "timeline icon-form";
      case "change_status":
        return "fa fa-exclamation";
      default:
        return "timeline icon-text";
    }
  }

}
